import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { useNotification } from '../../notifications/hooks/useNotification';
import { usePermissions } from '../../permissions/usePermissions';
import { useAuth } from '../../auth/hooks/useAuth';
import { AppModal } from '../../../components/ui/AppModal';
import { useRightsProfile } from '../hooks/useRightsProfile';
import { useRightsProfiles } from '../hooks/useRightsProfiles';
import { useRightsProfilesCatalogue } from '../hooks/useRightsProfilesCatalogue';
import {
  RightsProfileForm,
  type RightsProfileFormSubmit,
} from '../components/RightsProfileForm/RightsProfileForm';
import { hydratePermissionMatrix } from '../logic/permissionMatrix.logic';
import { ProfileNameConflictError } from '../errors/profile-conflict.error';

const USER_RIGHTS_LIST = '/my-ngo/user-rights';

/**
 * Edit page (T6): loads the profile via GET /rights-profiles/:id, seeds the shared
 * form from its (hydrated) permission matrix. Saving asks for confirmation first —
 * editing a profile applies immediately to all its users (spec §3.2 / §4.3) — then
 * update → success toast → back to the list. The editor is read-only without
 * `rights_profiles.modify`, and refreshes the caller's own rights after a save
 * (editing one's own profile must not keep stale `can()`).
 */
export function EditRightsProfilePage() {
  const { id } = useParams();
  const { t } = useTranslation('rightsProfiles');
  const navigate = useNavigate();
  const { notifySuccess } = useNotification();
  const { can } = usePermissions();
  const { refreshPermissions } = useAuth();

  const { profile, loading, error } = useRightsProfile(id);
  const {
    catalogue,
    loading: catalogueLoading,
    error: catalogueError,
  } = useRightsProfilesCatalogue();
  const { update, duplicate, saving } = useRightsProfiles();

  // Values pending confirmation — set on submit, applied once the user confirms.
  const [pending, setPending] = useState<RightsProfileFormSubmit | null>(null);
  const [nameError, setNameError] = useState<string | undefined>(undefined);

  const initialMatrix = useMemo(
    () => (profile && catalogue ? hydratePermissionMatrix(catalogue, profile.permissions) : null),
    [profile, catalogue],
  );

  // Saving is immediate for every user of the profile → ask before applying.
  const handleSubmit = (values: RightsProfileFormSubmit) => {
    if (!id) return;
    setPending(values);
  };

  const confirmSave = async () => {
    if (!id || !pending) return;
    setNameError(undefined);
    try {
      const updated = await update(id, {
        name: pending.name,
        description: pending.description || null,
        permissions: pending.permissions,
      });
      setPending(null);
      if (updated) {
        // Editing one's own profile changes the caller's effective rights — refresh
        // them so can() is not stale until the next reload (HOMCO-639).
        await refreshPermissions();
        notifySuccess(t('edit.saveSuccessCount', { count: updated.assigned_users_count }));
        navigate(USER_RIGHTS_LIST);
      }
    } catch (err) {
      // A taken name (409) is shown on the Name field, inputs kept — close the
      // confirm dialog so the user can correct it.
      setPending(null);
      if (ProfileNameConflictError.is(err)) {
        setNameError(t('form.nameConflict'));
        return;
      }
      throw err;
    }
  };

  // Duplicate (§4 / P7): a new profile "[name] (copie)" with the same rights, no
  // users attached. Uses the saved matrix, not any unsaved edits.
  const handleDuplicate = async () => {
    if (!id) return;
    const created = await duplicate(id);
    if (created) {
      notifySuccess(t('edit.duplicateSuccess'));
      navigate(`${USER_RIGHTS_LIST}/${created.id}/edit`);
    }
  };

  if (loading || catalogueLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || catalogueError || !profile || !catalogue || !initialMatrix) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('edit.loadError')}</Alert>
      </Box>
    );
  }

  return (
    <>
      <RightsProfileForm
        title={t('edit.title', { name: profile.name })}
        subtitle={t('edit.subtitle', { count: profile.assigned_users_count })}
        submitLabel={t('form.save')}
        usersCount={profile.assigned_users_count}
        // Maquette: the edit page uses the user-form look (plain surface, no band,
        // matrix/identity panel on the section surface).
        variant="form"
        headerActions={
          // Duplicate creates a new profile → gated by rights_profiles.create.
          can('rights_profiles', 'create') ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyOutlinedIcon />}
              onClick={handleDuplicate}
              disabled={saving}
              sx={{ borderRadius: 5, textTransform: 'none' }}
            >
              {t('form.duplicate')}
            </Button>
          ) : undefined
        }
        initialName={profile.name}
        initialDescription={profile.description ?? ''}
        catalogue={catalogue}
        initialMatrix={initialMatrix}
        nameError={nameError}
        readOnly={profile.is_locked || !can('rights_profiles', 'modify')}
        saving={saving}
        onSubmit={handleSubmit}
        onCancel={() => navigate(USER_RIGHTS_LIST)}
      />

      <AppModal
        open={pending !== null}
        onClose={() => setPending(null)}
        disableClose={saving}
        maxWidth="xs"
        titleId="edit-profile-save-title"
        icon={<SaveOutlinedIcon sx={th => ({ fontSize: th.iconSize.sm })} />}
        title={t('edit.confirmSaveTitle')}
        closeLabel={t('form.cancel')}
      >
        <DialogContent sx={{ pt: 2 }}>
          <Typography>{t('list.warning')}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            variant="outlined"
            onClick={() => setPending(null)}
            disabled={saving}
            sx={{ borderRadius: 5, textTransform: 'none' }}
          >
            {t('form.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={confirmSave}
            disabled={saving}
            sx={{ borderRadius: 5, textTransform: 'none' }}
          >
            {t('form.save')}
          </Button>
        </DialogActions>
      </AppModal>
    </>
  );
}
